import { windowIsReady } from './windowIsReady';

// Common regex patterns
const PATTERNS = {
	// Basic patterns
	ALPHANUMERIC_DASH_UNDERSCORE: /^[a-zA-Z0-9_-]+$/,
	ALPHANUMERIC_DASH_UNDERSCORE_DOT: /^[a-zA-Z0-9._-]+$/,

	// UTM pattern
	UTM: /^utm_[a-zA-Z0-9_-]+$/,

	// Click ID pattern
	CLICK_ID: /^[a-zA-Z0-9_-]*(clid|kid)$/,

	// Campaign pattern
	CAMPAIGN: /^[a-zA-Z0-9_-]*(campaign|camp)[a-zA-Z0-9_-]*$/,

	// Ad pattern
	AD: /^[a-zA-Z0-9_-]*(ad|ads)[a-zA-Z0-9_-]*$/,

	// Source pattern
	SOURCE: /^[a-zA-Z0-9_-]*(source|src)[a-zA-Z0-9_-]*$/,

	// Medium pattern
	MEDIUM: /^[a-zA-Z0-9_-]*(medium|med)[a-zA-Z0-9_-]*$/,
};

// Parameter groups with their validation patterns
const PARAMETER_GROUPS = {
	// UTM parameters
	utm_source: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	utm_medium: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	utm_campaign: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	utm_term: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	utm_content: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,

	// Platform click IDs (with dots allowed)
	fbclid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	fbc: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	fbp: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	gclid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	ttclid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	twclid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
	msclkid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,

	// Custom parameters
	uuid: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	campaign_id: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	adset_name: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	adset_id: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	ad_name: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	ad_id: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	ad_placement: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	test_tier: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
	m_test: PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
};

// Generic pattern matchers
const GENERIC_MATCHERS = [
	{ pattern: PATTERNS.UTM, name: 'utm' },
	{ pattern: PATTERNS.CLICK_ID, name: 'clid' },
	{ pattern: PATTERNS.CAMPAIGN, name: 'campaign' },
	{ pattern: PATTERNS.AD, name: 'ad' },
	{ pattern: PATTERNS.SOURCE, name: 'source' },
	{ pattern: PATTERNS.MEDIUM, name: 'medium' },
];

// Helper function to validate value against pattern
const validateValue = (
	value: string,
	pattern: RegExp,
	paramName: string,
	errorType: string,
): boolean => {
	if (!pattern.test(value)) {
		console.warn(`Invalid ${errorType} parameter: ${paramName}=${value}`);
		return false;
	}
	return true;
};

// Helper function to sanitize value
const sanitizeValue = (value: string): string => {
	return value
		.replace(/[<>'"&]/g, '') // Remove HTML/XML characters
		.trim();
};

// Validate and sanitize URL parameter
const validateParam = (key: string, value: string): string | null => {
	// Check if parameter has known pattern
	const knownPattern = PARAMETER_GROUPS[key as keyof typeof PARAMETER_GROUPS];

	if (knownPattern) {
		// Validate against known pattern
		if (!validateValue(value, knownPattern, key, 'known URL')) {
			return null;
		}
	} else {
		// Try to match generic patterns
		const matchedMatcher = GENERIC_MATCHERS.find(matcher =>
			matcher.pattern.test(key),
		);

		if (matchedMatcher) {
			// For generic patterns, use basic validation
			if (
				!validateValue(
					value,
					PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
					key,
					matchedMatcher.name,
				)
			) {
				return null;
			}
		} else {
			// Use safe default for unknown parameters
			if (
				!validateValue(
					key,
					PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE,
					key,
					'unknown parameter format',
				)
			) {
				return null;
			}
			if (
				!validateValue(
					value,
					PATTERNS.ALPHANUMERIC_DASH_UNDERSCORE_DOT,
					key,
					'safe parameter value',
				)
			) {
				return null;
			}
		}
	}

	// Sanitize and check length
	const sanitized = sanitizeValue(value);

	if (sanitized.length > 1000) {
		console.warn(`URL parameter too long: ${key}`);
		return null;
	}

	return sanitized;
};

export const getUrlParams: () => Record<string, string> = () => {
	if (!windowIsReady) {
		return {};
	}

	try {
		const searchParams = new URLSearchParams(window.location.search);
		const params: Record<string, string> = {};

		// Convert to array to avoid iterator issues
		const paramEntries = Array.from(searchParams.entries());
		paramEntries.forEach(([key, value]) => {
			const validatedValue = validateParam(key, value);
			if (validatedValue !== null) {
				params[key] = validatedValue;
			}
		});

		return params;
	} catch (error) {
		console.error('Error parsing URL parameters:', error);
		return {};
	}
};
